const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? "https://api.tappet.com.ar";
const BASE = `${API_URL}/api/v1`;

let accessToken: string | null = null;
let refreshInFlight: Promise<AuthResult> | null = null;
function refreshSession(): Promise<AuthResult> {
  refreshInFlight ??= request<AuthResult>("POST", "/auth/refresh", {})
    .then((value) => {
      setAccessToken(value.accessToken);
      return value;
    })
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
}

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

interface Envelope<T> {
  data?: T;
  error?: { code: string; message: string };
}

export async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  headers?: Record<string, string>,
  retried = false,
): Promise<T> {
  const response = await fetch(BASE + path, {
    method,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...headers,
    },
    body: body === undefined ? null : JSON.stringify(body),
  });
  if (response.status === 204) return undefined as T;
  const payload = (await response.json().catch(() => ({}))) as Envelope<T>;
  if (response.status === 401 && !path.startsWith("/auth/")) {
    if (!retried) {
      try {
        await refreshSession();
        return request<T>(method, path, body, headers, true);
      } catch {
        /* revocada o expirada */
      }
    }
    setAccessToken(null);
    window.dispatchEvent(new Event("tappet-session-expired"));
  }
  if (!response.ok) {
    const error = payload.error ?? {
      code: "UNKNOWN",
      message: "No pudimos completar la acción. Probá de nuevo.",
    };
    throw new ApiError(response.status, error.code, error.message);
  }
  return payload.data as T;
}

export interface AuthUser {
  id: string;
  email: string;
  displayName: string;
}

export interface AuthResult {
  user: AuthUser;
  accessToken: string;
  accessTokenExpiresInSeconds: number;
}

export type MembershipRole =
  "OWNER" | "ADMIN" | "EDITOR" | "ANALYST" | "VETERINARIAN" | "ASSISTANT" | "RECEPTION" | "AUDITOR";

export type ProWorkspaceType =
  "VETERINARY" | "COMMERCE" | "GROOMING" | "TRAINING" | "CARE" | "SHELTER" | "PARTNER";

export type ProCapability =
  | "VIEW_DASHBOARD"
  | "VIEW_CLIENTS"
  | "MANAGE_CLIENTS"
  | "MANAGE_SERVICES"
  | "MANAGE_APPOINTMENTS"
  | "VIEW_ACTIVITIES"
  | "COMPLETE_GROOMING"
  | "COMPLETE_TRAINING"
  | "COMPLETE_CARE"
  | "MANAGE_PRODUCTS"
  | "MANAGE_OFFERS"
  | "MANAGE_CAMPAIGNS"
  | "VIEW_ANALYTICS"
  | "CLINICAL_READ"
  | "CLINICAL_WRITE"
  | "CLINICAL_SIGN"
  | "MANAGE_TEAM"
  | "MANAGE_LOCATIONS"
  | "MANAGE_ORGANIZATION"
  | "MANAGE_ADOPTIONS";

export interface ProWorkspace {
  type: ProWorkspaceType;
  role: MembershipRole;
  isPrimary: boolean;
  capabilities: ProCapability[];
  entitlements: string[];
}

export interface ProOrganization {
  id: string;
  name: string;
  organizationType:
    "INDEPENDENT_VET" | "CLINIC" | "INDEPENDENT_PROFESSIONAL" | "PROFESSIONAL_BUSINESS";
  status: "ACTIVE";
  primaryVertical: ProWorkspaceType | null;
  availableWorkspaces: ProWorkspace[];
}

export interface ProContext {
  user: AuthUser;
  organizations: ProOrganization[];
  pendingOrganizations: Array<{
    organizationId: string;
    name: string;
    workspace: ProWorkspaceType;
    status: "DRAFT" | "PENDING_REVIEW";
  }>;
  preferredOrganizationId: string | null;
  preferredWorkspace: ProWorkspaceType | null;
}

export interface WorkspaceSettings {
  workspace: ProWorkspaceType;
  organization: {
    name: string;
    description: string | null;
    publicEmail: string | null;
    publicPhone: string | null;
    whatsappE164: string | null;
    publicAddress: string | null;
    cityArea: string | null;
    publicProfileEnabled: boolean;
  };
  settings: {
    scheduling?: {
      timezone?: string;
      defaultServiceDurationMinutes?: number;
      appointmentBufferMinutes?: number;
    };
    notifications?: { appointmentChanges?: boolean; dailySummary?: boolean };
    branding?: { accentColor?: string };
    operational?: { requireCompletionNotes?: boolean; requireServiceMedia?: boolean };
  };
}

export interface WorkspaceSettingsPatch {
  publicName?: string;
  description?: string | null;
  contact?: {
    email?: string | null;
    phone?: string | null;
    whatsappE164?: string | null;
    address?: string | null;
    cityArea?: string | null;
  };
  publicProfileEnabled?: boolean;
  scheduling?: {
    timezone?: string;
    defaultServiceDurationMinutes?: number;
    appointmentBufferMinutes?: number;
  };
  notifications?: { appointmentChanges?: boolean; dailySummary?: boolean };
  branding?: { accentColor?: string };
  operational?: { requireCompletionNotes?: boolean; requireServiceMedia?: boolean };
}

export type CommercialProductStatus = "DRAFT" | "ACTIVE" | "ARCHIVED";
export interface CommercialProduct {
  id: string;
  name: string;
  description: string;
  brand: string;
  category: string;
  status: CommercialProductStatus;
  species: Array<"DOG" | "CAT">;
  imageUrls: string[];
  updatedAt: string;
  offers: Array<{
    id: string;
    availability: "AVAILABLE" | "UNKNOWN" | "UNAVAILABLE";
    priceCents: number | null;
    currency: string | null;
    active: boolean;
  }>;
}

export interface CommercialCampaign {
  id: string;
  name: string;
  status: "DRAFT" | "PENDING_REVIEW" | "APPROVED" | "ACTIVE" | "PAUSED" | "ENDED" | "REJECTED";
  objective: string;
  startsAt: string;
  endsAt: string;
  sensitive: boolean;
  placements: string[];
  targetingRules: Record<string, unknown>;
  frequencyCap: number;
  frequencyWindowDays: number;
  priority: number;
  products: Array<{ product: Pick<CommercialProduct, "id" | "name" | "brand"> }>;
}

export interface CommercialAnalytics {
  organizationId: string;
  from: string;
  to: string;
  totals: {
    reach: number;
    impressions: number;
    clicks: number;
    productViews: number;
    contacts: number;
    bookings: number;
    benefitOpens: number;
  };
  privacy: "AGGREGATED_ONLY";
}

export interface CommercialInventoryItem {
  id: string;
  product: { id: string; name: string; brand: string };
  inventory: {
    id: string;
    quantity: number;
    reorderPoint: number;
    movements: Array<{
      id: string;
      type: "INBOUND" | "OUTBOUND" | "ADJUSTMENT";
      quantityDelta: number;
      reason: string | null;
      createdAt: string;
    }>;
  } | null;
}

export interface CreateCommercialProductInput {
  name: string;
  description: string;
  brand: string;
  category: string;
  species: Array<"DOG" | "CAT">;
  status: CommercialProductStatus;
  imageUrls: string[];
  lifeStages: string[];
  sizes: string[];
  sexes: string[];
  positiveSignalKeys: string[];
  negativeSignalKeys: string[];
  contraindicationKeys: string[];
  recommendationPolicy: "GENERAL" | "PROFESSIONAL_GUIDANCE" | "PRESCRIPTION_REQUIRED";
  featured: boolean;
}

export type OperationalWorkspaceType = Extract<ProWorkspaceType, "GROOMING" | "TRAINING" | "CARE">;

export interface OperationalOverview {
  workspace: OperationalWorkspaceType;
  agenda?: { timezone: string; dayFrom: string; dayTo: string; weekTo: string };
  metrics: {
    todayAppointments: number;
    upcomingAppointments: number;
    clientCount: number;
    activityCount: number;
    waitingAppointments: number;
    inServiceAppointments: number;
    completedToday: number;
    noShowsToday: number;
    nextAppointment: { startsAt: string; petName: string } | null;
  };
}

export interface ProfessionalServiceItem {
  id: string;
  name: string;
  description: string | null;
  durationMinutes: number;
  active: boolean;
  priceCents: number | null;
  currency: string | null;
  bookingPolicy: "AUTO_CONFIRM" | "MANUAL_APPROVAL";
}

export interface OperationalAppointment {
  scheduleTimezone?: string | null;
  proposedStartsAt?: string | null;
  id: string;
  startsAt: string;
  durationMinutes: number;
  title: string;
  status: AppointmentStatus;
  notes: string | null;
  pet: {
    id: string;
    name: string;
    species: "DOG" | "CAT";
    breed: string | null;
    size: string | null;
  };
  service: { id: string; name: string; durationMinutes: number } | null;
}

export interface OperationalClient {
  id: string;
  requestedAt: string;
  pet: {
    id: string;
    publicSlug: string;
    name: string;
    species: "DOG" | "CAT";
    breed: string | null;
    size: string | null;
    birthDate: string | null;
    color: string | null;
    mediaAssets: Array<{ id: string; url: string | null }>;
  };
}

export interface OperationalActivity {
  id: string;
  happenedAt: string;
  title: string;
  notes: string | null;
  metadata: Record<string, unknown> | null;
  pet: { id: string; name: string; species: "DOG" | "CAT"; breed: string | null };
  service: { id: string; name: string } | null;
  groomingSession?: {
    coatCondition: string | null;
    behavior: string | null;
    productsUsed: string[];
    incidents: string | null;
    nextVisitSuggestedAt: string | null;
    media: Array<{ kind: "BEFORE" | "AFTER"; assetId: string; url: string | null }>;
  } | null;
  trainingSession?: {
    objectiveWorked: string | null;
    exercises: string[];
    observedBehavior: string | null;
    progress: string | null;
    difficulties: string | null;
    homework: string | null;
    nextSessionAt: string | null;
    trainingPlan: { id: string; name: string } | null;
    media: Array<{
      assetId: string;
      contentType: string;
      filename: string | null;
      url: string | null;
    }>;
  } | null;
  careSession?: {
    checkedInAt: string | null;
    startedAt: string;
    completedAt: string;
    observations: string | null;
    incidents: string | null;
    media: Array<{
      assetId: string;
      contentType: string;
      filename: string | null;
      url: string | null;
    }>;
  } | null;
}

export interface TrainingPlan {
  id: string;
  name: string;
  objective: string;
  notes: string | null;
  startsAt: string;
  status: "ACTIVE" | "COMPLETED" | "PAUSED" | "ARCHIVED";
  pet: { id: string; name: string; species: "DOG" | "CAT"; breed: string | null };
  goals: Array<{
    id: string;
    description: string;
    priority: number;
    status: "PENDING" | "IN_PROGRESS" | "ACHIEVED" | "CANCELLED";
    progress: number;
    targetAt: string | null;
  }>;
  sessions: Array<{
    id: string;
    completedAt: string;
    progress: string | null;
    nextSessionAt: string | null;
  }>;
}

export interface CarePetProfile {
  id: string;
  organizationId: string;
  petId: string;
  behavior: string | null;
  equipment: string | null;
  restrictions: string | null;
  careInstructions: string | null;
  authorizedContact: string | null;
  emergencyContact: string | null;
  updatedAt: string;
}

export interface ProfessionalMediaUploadIntent {
  assetId: string;
  uploadUrl: string;
  method: "PUT";
  headers: { "Content-Type": string };
  expiresAt: string;
  maxBytes: number;
}

export interface OperationalReport {
  workspace: OperationalWorkspaceType;
  from: string;
  to: string;
  totals: {
    appointments: number;
    completed: number;
    noShows: number;
    revenueCents: number;
    uniqueClients: number;
    recurringClients: number;
  };
  services: Array<{
    serviceId: string;
    name: string;
    appointments: number;
    completed: number;
    revenueCents: number;
  }>;
}

export interface ShelterAnimal {
  id: string;
  status: "IN_CARE" | "TRANSFER_PENDING" | "TRANSFERRED" | "RELEASED";
  intakeAt: string;
  source: string | null;
  pet: {
    id: string;
    publicSlug: string;
    name: string;
    species: "DOG" | "CAT";
    breed: string | null;
    clinicalSex: "MALE" | "FEMALE" | null;
    size: string | null;
    color: string | null;
    birthDate: string | null;
    bio: string | null;
    mediaAssets: Array<{ id: string; purpose: string }>;
  };
}

export interface AdoptionListing {
  id: string;
  slug: string;
  status: "DRAFT" | "AVAILABLE" | "PAUSED" | "RESERVED" | "ADOPTED" | "ARCHIVED";
  headline: string;
  description: string;
  idealHome: string | null;
  publicHealthSummary: string | null;
  zone: string | null;
  publishedAt: string | null;
  adoptedAt: string | null;
  pet: Pick<
    ShelterAnimal["pet"],
    | "id"
    | "publicSlug"
    | "name"
    | "species"
    | "breed"
    | "clinicalSex"
    | "size"
    | "color"
    | "birthDate"
  >;
  media: Array<{ mediaAsset: { id: string; bucket: string; objectKey: string; status: string } }>;
}

export interface AdoptionApplication {
  id: string;
  applicantName: string;
  email: string;
  phone: string | null;
  message: string | null;
  status:
    "RECEIVED" | "UNDER_REVIEW" | "INTERVIEW" | "APPROVED" | "REJECTED" | "CANCELLED" | "COMPLETED";
  internalNotes: string | null;
  adoptionListing: { id: string; headline: string; pet: { id: string; name: string } };
  updatedAt: string;
}

export interface CrmOrganization {
  id: string;
  name: string;
  type: "INDEPENDENT_VET" | "CLINIC" | "INDEPENDENT_PROFESSIONAL" | "PROFESSIONAL_BUSINESS";
  role: MembershipRole;
}

export interface CrmContext {
  professional: {
    fullName: string;
    licenseNumber: string;
    jurisdiction: string;
    specialty: string | null;
    status: "PENDING_REVIEW" | "APPROVED" | "REJECTED" | "SUSPENDED";
  } | null;
  organizations: CrmOrganization[];
}

export type AppointmentStatus =
  "PLANNED" | "CONFIRMED" | "CHECKED_IN" | "IN_PROGRESS" | "COMPLETED" | "CANCELLED" | "NO_SHOW";

export interface CrmAppointment {
  id: string;
  petId: string;
  petName: string;
  organizationId: string;
  organizationLocationId: string | null;
  serviceId: string | null;
  assignedProfessionalUserId: string | null;
  bookingSource: string | null;
  proposedStartsAt: string | null;
  startsAt: string;
  durationMinutes: number;
  title: string;
  providerName: string | null;
  location: string | null;
  notes: string | null;
  status: AppointmentStatus;
  reminderAt: string | null;
  reminderMinutesBefore: number | null;
  confirmedAt: string | null;
  checkedInAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  cancelledAt: string | null;
  noShowAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CrmOperationalAppointment {
  id: string;
  petId: string;
  petName: string;
  petSpecies: "DOG" | "CAT";
  petBreed: string | null;
  organizationId: string;
  organizationLocationId: string | null;
  startsAt: string;
  durationMinutes: number;
  title: string;
  providerName: string | null;
  location: string | null;
  notes: string | null;
  status: AppointmentStatus;
  confirmedAt: string | null;
  checkedInAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  cancelledAt: string | null;
  noShowAt: string | null;
  waitMinutes: number | null;
  delayMinutes: number;
  capabilities: {
    canConfirm: boolean;
    canCheckIn: boolean;
    canStart: boolean;
    canComplete: boolean;
    canCancel: boolean;
    canNoShow: boolean;
    canOpenAttention: boolean;
  };
}

export interface CrmOperationsOverview {
  day: string;
  timezone: "America/Argentina/Buenos_Aires";
  generatedAt: string;
  viewer: {
    role: MembershipRole;
    canOperate: boolean;
    canStartAttention: boolean;
  };
  pulse: {
    scheduledCount: number;
    upcomingCount: number;
    waitingCount: number;
    inProgressCount: number;
    completedCount: number;
    cancelledCount: number;
    noShowCount: number;
  };
  performance: {
    windowDays: 7 | 30;
    medianWaitMinutes: number | null;
    averageAttentionMinutes: number | null;
    onTimeStartRate: number | null;
    noShowRate: number | null;
  };
  appointments: CrmOperationalAppointment[];
  trend: Array<{
    day: string;
    scheduledCount: number;
    completedCount: number;
    cancelledCount: number;
    noShowCount: number;
    averageWaitMinutes: number | null;
  }>;
}

export interface CrmDashboard {
  organization: CrmOrganization;
  metrics: {
    patientCount: number;
    pendingAccessCount: number;
    todayAppointmentCount: number;
    dueVaccinationCount: number;
    activeDraftCount: number;
  };
  todayAppointments: CrmAppointment[];
  recentPatients: Array<{
    accessId: string;
    scope: AccessScope;
    id: string;
    name: string;
    species: "DOG" | "CAT";
    breed: string | null;
    isLost: boolean;
    updatedAt: string;
  }>;
}

export type AccessScope = "READ_ONLY" | "CLINICAL" | "PRIMARY";

export interface CrmPatient {
  accessId: string;
  scope: AccessScope;
  linkedAt: string;
  id: string;
  publicSlug: string;
  name: string;
  species: "DOG" | "CAT";
  breed: string | null;
  birthDate: string | null;
  isLost: boolean;
  clinicalDetailsAvailable: boolean;
  weightKg: number | null;
  activeTagCode: string | null;
  consultationCount: number | null;
  vaccinationCount: number | null;
}

export type PatientHistoryCollection = "consultations" | "vaccinations" | "medications";
export interface PatientHistoryPage {
  offset: number;
  limit: number;
  total: number;
  hasMore: boolean;
}
export interface PatientHistoryQuery {
  consultationsOffset?: number;
  vaccinationsOffset?: number;
  medicationsOffset?: number;
  limit?: number;
}
export interface CrmPatientDetail {
  pagination?: Record<PatientHistoryCollection, PatientHistoryPage>;
  tutorReportedMedications?: Array<Record<string, unknown> & { id: string; name: string }>;
  access: { id: string; scope: AccessScope; linkedAt: string | null };
  capabilities: {
    canWriteClinical: boolean;
    signer: {
      fullName: string;
      licenseNumber: string;
      jurisdiction: string;
    } | null;
  };
  patient: {
    id: string;
    publicSlug: string;
    name: string;
    species: "DOG" | "CAT";
    breed: string | null;
    sex: string | null;
    birthDate: string | null;
    isLost: boolean;
    activeTagCode: string | null;
    reproductive: Record<string, unknown> | null;
  };
  medicalSummary: {
    allergies: string | null;
    conditions: string | null;
    medications: string | null;
    weightKg: number | null;
    bloodType: string | null;
    notes: string | null;
    criticalAlerts: string | null;
    sourceType: ClinicalSourceType;
    verifiedAt: string | null;
    updatedAt: string;
  } | null;
  vaccinations: Array<{
    id: string;
    name: string;
    administeredAt: string;
    nextDueAt: string | null;
    providerName: string | null;
    professionalName: string | null;
    professionalLicense: string | null;
    manufacturer: string | null;
    batchNumber: string | null;
    route: MedicationRoute | null;
    sourceType: ClinicalSourceType;
    notes: string | null;
    createdAt: string;
  }>;
  medications: CrmMedication[];
  consultations: Array<{
    id: string;
    organizationId: string | null;
    status: "SIGNED" | "AMENDED" | "VOIDED";
    occurredAt: string;
    reason: string;
    findings: string | null;
    anamnesis: string | null;
    physicalExam: string | null;
    assessment: string | null;
    diagnosis: string | null;
    treatment: string | null;
    indications: string | null;
    followUpAt: string | null;
    accessMode: string;
    sourceType: ClinicalSourceType;
    organizationName: string | null;
    authorVetName: string;
    authorVetLicense: string | null;
    signedAt: string;
    voidReason: string | null;
    voidedAt: string | null;
    canAmend: boolean;
    canAttach: boolean;
    canVoid: boolean;
    attachmentCount: number;
    amendments: Array<{
      id: string;
      content: string;
      authorVetName: string;
      authorVetLicense: string | null;
      createdAt: string;
    }>;
  }>;
  problems: Array<{
    id: string;
    name: string;
    description: string | null;
    status: "ACTIVE" | "RESOLVED" | "CHRONIC" | "UNDER_FOLLOW_UP";
    sourceType: ClinicalSourceType;
    authorName: string | null;
    authorLicense: string | null;
    updatedAt: string;
  }>;
  preventiveCare: Array<Record<string, unknown>>;
  appointments: CrmAppointment[];
}

export interface CrmAccess {
  id: string;
  organizationId: string;
  petId: string;
  petName: string;
  petSpecies: "DOG" | "CAT";
  petPublicSlug: string;
  scope: AccessScope;
  status: "PENDING" | "ACTIVE" | "REJECTED" | "REVOKED";
  message: string | null;
  requestedAt: string;
  decidedAt: string | null;
  revokedAt: string | null;
  requestedByName: string;
}

export interface AppointmentInput {
  startsAt: string;
  durationMinutes: number;
  title: string;
  locationId?: string;
  location?: string;
  notes?: string;
  reminderMinutesBefore: 30 | 60 | 120 | 1440 | 2880 | 10080 | null;
}

export interface ConsultationInput {
  occurredAt?: string;
  reason: string;
  findings?: string;
  anamnesis?: string;
  physicalExam?: string;
  assessment?: string;
  diagnosis?: string;
  treatment?: string;
  indications?: string;
  followUpAt?: string;
  problems?: Array<{
    name: string;
    description?: string | null;
    status: "ACTIVE" | "RESOLVED" | "CHRONIC" | "UNDER_FOLLOW_UP";
  }>;
}

export type CrmConsultation = CrmPatientDetail["consultations"][number];
export type CrmVaccination = CrmPatientDetail["vaccinations"][number];

export type MedicationRoute =
  "ORAL" | "TOPICAL" | "OPHTHALMIC" | "OTIC" | "INJECTABLE" | "INHALED" | "OTHER";
export type MedicationStatus = "ACTIVE" | "COMPLETED" | "DISCONTINUED" | "SUSPENDED" | "CANCELLED";
export type ClinicalSourceType =
  "TUTOR_REPORTED" | "VETERINARIAN_RECORDED" | "VETERINARIAN_VERIFIED" | "IMPORTED" | "SYSTEM";

export interface CrmMedication {
  publicCareInstructions?: string | null;
  id: string;
  consultationId: string | null;
  name: string;
  activeIngredient: string | null;
  dosage: string;
  doseValue: string | null;
  doseUnit: string | null;
  frequency: string;
  route: MedicationRoute;
  startAt: string;
  endAt: string | null;
  instructions: string | null;
  status: MedicationStatus;
  stoppedAt: string | null;
  stoppedReason: string | null;
  prescriberName: string;
  prescriberLicense: string | null;
  organizationName: string;
  createdAt: string;
  sourceType: ClinicalSourceType;
  lastTutorReport: { type: string; note: string | null; occurredAt: string } | null;
  canStop: boolean;
}

export interface MedicationInput {
  publicCareInstructions?: string;
  consultationId: string;
  name: string;
  activeIngredient?: string;
  dosage: string;
  doseValue?: number;
  doseUnit?: string;
  frequency: string;
  route: MedicationRoute;
  startAt: string;
  endAt?: string;
  instructions?: string;
}

export interface VaccinationInput {
  name: string;
  administeredAt: string;
  nextDueAt?: string;
  providerName?: string;
  manufacturer?: string;
  batchNumber?: string;
  route?: MedicationRoute;
  notes?: string;
}

export type ClinicalDraftSource = "VET_MOBILE" | "CRM";

export interface ClinicalDraftFields {
  reason: string;
  findings: string;
  diagnosis: string;
  treatment: string;
  alertsReviewed: boolean;
}

export interface CrmClinicalDraft extends ClinicalDraftFields {
  id: string;
  organizationId: string;
  petId: string;
  petName: string;
  petSpecies: "DOG" | "CAT";
  petBreed: string | null;
  authorUserId: string;
  authorName: string;
  source: ClinicalDraftSource;
  status: "ACTIVE";
  revision: number;
  expiresAt: string;
  createdAt: string;
  updatedAt: string;
  canEdit: boolean;
  canSign: boolean;
}

export interface ClinicalDraftSigned {
  draftId: string;
  consultationId: string;
  petId: string;
  signedAt: string;
}

export interface ClinicalAttachment {
  id: string;
  originalFilename: string | null;
  contentType: string;
  sizeBytes: number;
  uploadedAt: string | null;
  url: string;
  category: string;
  title: string;
  description: string | null;
  documentDate: string | null;
  sourceType: ClinicalSourceType;
  authorName: string | null;
  authorLicense: string | null;
}

export interface ClinicalUploadIntent {
  assetId: string;
  uploadUrl: string;
  method: "PUT";
  headers: { "Content-Type": string };
  expiresAt: string;
  maxBytes: number;
}

export interface CrmTeamMember {
  id: string;
  userId: string;
  displayName: string;
  email: string;
  role: MembershipRole;
  joinedAt: string;
  canManage: boolean;
  status?: "ACTIVE" | "INVITED" | "REVOKED";
  accountStatus?: string;
  workspaceScopes?: ProWorkspaceType[];
}

export interface CrmTeamInvitation {
  id: string;
  email: string;
  role: Exclude<MembershipRole, "OWNER">;
  expiresAt: string;
  createdAt: string;
  status?: "PENDING" | "ACCEPTED" | "REVOKED" | "EXPIRED";
  canRevoke?: boolean;
}

export interface CrmTeamOverview {
  viewerRole: MembershipRole;
  canManage: boolean;
  members: CrmTeamMember[];
  invitations: CrmTeamInvitation[];
  assignableRoles?: Array<Exclude<MembershipRole, "OWNER">>;
}

function teamPath(organizationId: string, workspace: ProWorkspaceType): string {
  return workspace === "VETERINARY"
    ? `/crm/organizations/${organizationId}/team`
    : `/pro/organizations/${organizationId}/workspaces/${workspace}/team`;
}

export interface CrmTeamInvitationCreated {
  id: string;
  email: string;
  role: Exclude<MembershipRole, "OWNER">;
  expiresAt: string;
  acceptToken: string;
}

export interface CrmLocation {
  id: string;
  organizationId: string;
  name: string;
  address: string | null;
  phone: string | null;
  timezone: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export type CrmTaskStatus = "OPEN" | "IN_PROGRESS" | "COMPLETED" | "CANCELLED";
export type CrmTaskPriority = "LOW" | "NORMAL" | "HIGH";

export interface CrmTask {
  id: string;
  organizationId: string;
  pet: { id: string; name: string } | null;
  title: string;
  description: string | null;
  status: CrmTaskStatus;
  priority: CrmTaskPriority;
  dueAt: string | null;
  assignedUserId: string | null;
  assignedName: string | null;
  createdByUserId: string;
  createdByName: string;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
  capabilities: { canEdit: boolean; canAssign: boolean; canTransition: boolean };
}

export const api = {
  login: (input: { email: string; password: string }) =>
    request<AuthResult>("POST", "/auth/login", input),
  refresh: () => refreshSession(),
  logout: () => request<void>("POST", "/auth/logout", {}),
  context: () => request<CrmContext>("GET", "/crm/context"),
  proContext: () => request<ProContext>("GET", "/pro/me"),
  selectProContext: (organizationId: string, workspace: ProWorkspaceType) =>
    request<{ organizationId: string; workspace: ProWorkspaceType; capabilities: ProCapability[] }>(
      "POST",
      "/pro/selection",
      { organizationId, workspace },
    ),
  workspaceSettings: (organizationId: string, workspace: ProWorkspaceType) =>
    request<WorkspaceSettings>(
      "GET",
      `/pro/organizations/${organizationId}/workspaces/${workspace}/settings`,
    ),
  availabilityConfiguration: (organizationId: string, workspace: ProWorkspaceType) =>
    request<AvailabilityConfiguration>(
      "GET",
      `/pro/organizations/${organizationId}/workspaces/${workspace}/availability`,
    ),
  saveAvailability: (
    organizationId: string,
    workspace: ProWorkspaceType,
    kind: "rules" | "blocks",
    input: Record<string, unknown>,
    id?: string,
  ) =>
    request(
      id ? "PATCH" : "POST",
      `/pro/organizations/${organizationId}/workspaces/${workspace}/availability/${kind}${id ? `/${id}` : ""}`,
      input,
    ),
  retireAvailability: (
    organizationId: string,
    workspace: ProWorkspaceType,
    kind: "rules" | "blocks",
    id: string,
  ) =>
    request(
      "DELETE",
      `/pro/organizations/${organizationId}/workspaces/${workspace}/availability/${kind}/${id}`,
    ),
  updateWorkspaceSettings: (
    organizationId: string,
    workspace: ProWorkspaceType,
    input: WorkspaceSettingsPatch,
  ) =>
    request<WorkspaceSettings>(
      "PATCH",
      `/pro/organizations/${organizationId}/workspaces/${workspace}/settings`,
      input,
    ),
  proOnboard: (input: {
    name: string;
    workspace: ProWorkspaceType;
    description?: string;
    publicEmail?: string;
    phone?: string;
    cityArea?: string;
  }) =>
    request<{
      organizationId: string;
      workspace: ProWorkspaceType;
      status: "APPROVED" | "PENDING_REVIEW";
    }>("POST", "/pro/onboarding", input),
  myVetRegistration: () => request<{ status: string } | null>("GET", "/vet/registration"),
  submitVetRegistration: (input: {
    fullName: string;
    licenseNumber: string;
    jurisdiction: string;
    specialty?: string;
    phone: string;
    acceptProfessionalTerms: true;
    termsVersion: string;
  }) => request<{ status: string }>("POST", "/vet/registration", input),
  myProfessionalProfiles: () =>
    request<Array<{ type: "GROOMING" | "TRAINER" | "DOG_WALKER"; status: string }>>(
      "GET",
      "/professional/profiles",
    ),
  submitProfessionalProfile: (input: {
    type: "GROOMING" | "TRAINER" | "DOG_WALKER";
    displayName: string;
    phone: string;
    bio?: string;
    acceptProfessionalTerms: true;
    termsVersion: string;
  }) => request<{ status: string }>("POST", "/professional/profile", input),
  commercialProducts: (organizationId: string) =>
    request<CommercialProduct[]>("GET", `/partners/organizations/${organizationId}/products`),
  createCommercialProduct: (organizationId: string, input: CreateCommercialProductInput) =>
    request<CommercialProduct>("POST", `/partners/organizations/${organizationId}/products`, input),
  updateCommercialProduct: (
    organizationId: string,
    productId: string,
    input: Partial<CreateCommercialProductInput>,
  ) =>
    request<CommercialProduct>(
      "PATCH",
      `/partners/organizations/${organizationId}/products/${productId}`,
      input,
    ),
  upsertCommercialOffer: (
    organizationId: string,
    productId: string,
    input: {
      availability: "AVAILABLE" | "UNKNOWN" | "UNAVAILABLE";
      priceCents: number | null;
      currency: string | null;
      active: boolean;
    },
  ) =>
    request<CommercialProduct["offers"][number]>(
      "POST",
      `/partners/organizations/${organizationId}/products/${productId}/offer`,
      input,
    ),
  commercialInventory: (organizationId: string) =>
    request<CommercialInventoryItem[]>(
      "GET",
      `/partners/organizations/${organizationId}/inventory`,
    ),
  recordCommercialInventoryMovement: (
    organizationId: string,
    offerId: string,
    input: {
      type: "INBOUND" | "OUTBOUND" | "ADJUSTMENT";
      quantityDelta: number;
      reason?: string;
      reorderPoint?: number;
    },
  ) =>
    request<{ inventory: CommercialInventoryItem["inventory"]; movement: unknown }>(
      "POST",
      `/partners/organizations/${organizationId}/offers/${offerId}/inventory/movements`,
      input,
    ),
  commercialCampaigns: (organizationId: string) =>
    request<CommercialCampaign[]>("GET", `/partners/organizations/${organizationId}/campaigns`),
  createCommercialCampaign: (
    organizationId: string,
    input: {
      name: string;
      objective: "REACH" | "PRODUCT_VIEWS" | "CONTACTS" | "BOOKINGS" | "BENEFIT_REDEMPTIONS";
      placements: string[];
      targetingRules: Record<string, unknown>;
      productIds: string[];
      startsAt: string;
      endsAt: string;
      frequencyCap: number;
      frequencyWindowDays: number;
      priority: number;
    },
  ) =>
    request<CommercialCampaign>(
      "POST",
      `/partners/organizations/${organizationId}/campaigns`,
      input,
    ),
  updateCommercialCampaign: (
    organizationId: string,
    campaignId: string,
    input: Partial<{
      name: string;
      objective: "REACH" | "PRODUCT_VIEWS" | "CONTACTS" | "BOOKINGS" | "BENEFIT_REDEMPTIONS";
      placements: string[];
      targetingRules: Record<string, unknown>;
      productIds: string[];
      startsAt: string;
      endsAt: string;
      frequencyCap: number;
      frequencyWindowDays: number;
      priority: number;
    }>,
  ) =>
    request<CommercialCampaign>(
      "PATCH",
      `/partners/organizations/${organizationId}/campaigns/${campaignId}`,
      input,
    ),
  changeCommercialCampaignStatus: (
    organizationId: string,
    campaignId: string,
    status: CommercialCampaign["status"],
  ) =>
    request<CommercialCampaign>(
      "PATCH",
      `/partners/organizations/${organizationId}/campaigns/${campaignId}/status`,
      { status },
    ),
  commercialAnalytics: (organizationId: string) =>
    request<CommercialAnalytics>("GET", `/partners/organizations/${organizationId}/analytics`),
  operationalOverview: (organizationId: string, workspace: OperationalWorkspaceType) =>
    request<OperationalOverview>(
      "GET",
      `/pro/organizations/${organizationId}/workspaces/${workspace}/overview`,
    ),
  operationalServices: (organizationId: string, workspace: OperationalWorkspaceType) =>
    request<ProfessionalServiceItem[]>(
      "GET",
      `/pro/organizations/${organizationId}/workspaces/${workspace}/services`,
    ),
  proposeAppointmentTime: (
    organizationId: string,
    appointmentId: string,
    proposedStartsAt: string,
  ) =>
    request("POST", `/crm/organizations/${organizationId}/bookings/${appointmentId}/decision`, {
      decision: "PROPOSE_TIME",
      proposedStartsAt,
    }),
  createOperationalService: (
    organizationId: string,
    workspace: OperationalWorkspaceType,
    input: {
      name: string;
      description?: string;
      durationMinutes?: number;
      priceCents: number | null;
      currency: string | null;
      bookingPolicy: "AUTO_CONFIRM" | "MANUAL_APPROVAL";
      assignedProfessionalUserId: string | null;
      locationId: string | null;
      capacity: number;
    },
  ) =>
    request<ProfessionalServiceItem>(
      "POST",
      `/pro/organizations/${organizationId}/workspaces/${workspace}/services`,
      input,
    ),
  operationalAppointments: (
    organizationId: string,
    workspace: OperationalWorkspaceType,
    range?: { from: string; to: string },
  ) =>
    request<OperationalAppointment[]>(
      "GET",
      `/pro/organizations/${organizationId}/workspaces/${workspace}/appointments${range ? `?from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}` : ""}`,
    ),
  operationalClients: (organizationId: string, workspace: OperationalWorkspaceType) =>
    request<OperationalClient[]>(
      "GET",
      `/pro/organizations/${organizationId}/workspaces/${workspace}/clients`,
    ),
  operationalActivities: (organizationId: string, workspace: OperationalWorkspaceType) =>
    request<OperationalActivity[]>(
      "GET",
      `/pro/organizations/${organizationId}/workspaces/${workspace}/activities`,
    ),
  trainingPlans: (organizationId: string) =>
    request<TrainingPlan[]>(
      "GET",
      `/pro/organizations/${organizationId}/workspaces/TRAINING/plans`,
    ),
  createTrainingPlan: (
    organizationId: string,
    input: { petId: string; name: string; objective: string; notes?: string; startsAt: string },
  ) =>
    request<TrainingPlan>(
      "POST",
      `/pro/organizations/${organizationId}/workspaces/TRAINING/plans`,
      input,
    ),
  createTrainingGoal: (
    organizationId: string,
    planId: string,
    input: { description: string; priority: number; targetAt?: string },
  ) =>
    request<TrainingPlan["goals"][number]>(
      "POST",
      `/pro/organizations/${organizationId}/workspaces/TRAINING/plans/${planId}/goals`,
      input,
    ),
  updateTrainingGoal: (
    organizationId: string,
    goalId: string,
    input: {
      status?: TrainingPlan["goals"][number]["status"];
      progress?: number;
      targetAt?: string | null;
    },
  ) =>
    request<TrainingPlan["goals"][number]>(
      "PATCH",
      `/pro/organizations/${organizationId}/workspaces/TRAINING/goals/${goalId}`,
      input,
    ),
  carePetProfile: (organizationId: string, petId: string) =>
    request<CarePetProfile | null>(
      "GET",
      `/pro/organizations/${organizationId}/workspaces/CARE/pets/${petId}/profile`,
    ),
  saveCarePetProfile: (
    organizationId: string,
    petId: string,
    input: Partial<Omit<CarePetProfile, "id" | "organizationId" | "petId" | "updatedAt">>,
  ) =>
    request<CarePetProfile>(
      "PATCH",
      `/pro/organizations/${organizationId}/workspaces/CARE/pets/${petId}/profile`,
      input,
    ),
  operationalReports: (organizationId: string, workspace: OperationalWorkspaceType) =>
    request<OperationalReport>(
      "GET",
      `/pro/organizations/${organizationId}/workspaces/${workspace}/reports`,
    ),
  startOperationalAppointment: (
    organizationId: string,
    workspace: OperationalWorkspaceType,
    appointmentId: string,
  ) =>
    request<OperationalAppointment>(
      "PATCH",
      `/pro/organizations/${organizationId}/workspaces/${workspace}/appointments/${appointmentId}/start`,
      {},
    ),
  transitionOperationalAppointment: (
    organizationId: string,
    workspace: OperationalWorkspaceType,
    appointmentId: string,
    status: "CONFIRMED" | "CHECKED_IN" | "CANCELLED" | "NO_SHOW",
  ) =>
    request<OperationalAppointment>(
      "PATCH",
      `/pro/organizations/${organizationId}/workspaces/${workspace}/appointments/${appointmentId}/status`,
      { status },
    ),
  completeOperationalAppointment: (
    organizationId: string,
    workspace: OperationalWorkspaceType,
    appointmentId: string,
    input: { notes?: string; metadata?: Record<string, unknown>; mediaAssetIds?: string[] },
  ) =>
    request<OperationalActivity>(
      "POST",
      `/pro/organizations/${organizationId}/workspaces/${workspace}/appointments/${appointmentId}/complete`,
      input,
    ),
  createOperationalMediaUploadIntent: (
    organizationId: string,
    workspace: OperationalWorkspaceType,
    appointmentId: string,
    input: {
      fileName: string;
      contentType: "image/jpeg" | "image/png" | "image/webp" | "video/mp4";
      sizeBytes: number;
    },
  ) =>
    request<ProfessionalMediaUploadIntent>(
      "POST",
      `/pro/organizations/${organizationId}/workspaces/${workspace}/appointments/${appointmentId}/media/upload-intents`,
      input,
    ),
  completeOperationalMediaUpload: (
    organizationId: string,
    workspace: OperationalWorkspaceType,
    appointmentId: string,
    assetId: string,
  ) =>
    request<{ assetId: string; status: "READY"; uploadedAt: string }>(
      "POST",
      `/pro/organizations/${organizationId}/workspaces/${workspace}/appointments/${appointmentId}/media/${assetId}/complete`,
      {},
    ),
  createGroomingMediaUploadIntent: (
    organizationId: string,
    appointmentId: string,
    input: {
      fileName: string;
      contentType: "image/jpeg" | "image/png" | "image/webp";
      sizeBytes: number;
    },
  ) =>
    request<ProfessionalMediaUploadIntent>(
      "POST",
      `/pro/organizations/${organizationId}/workspaces/GROOMING/appointments/${appointmentId}/media/upload-intents`,
      input,
    ),
  completeGroomingMediaUpload: (organizationId: string, appointmentId: string, assetId: string) =>
    request<{ assetId: string; status: "READY"; uploadedAt: string }>(
      "POST",
      `/pro/organizations/${organizationId}/workspaces/GROOMING/appointments/${appointmentId}/media/${assetId}/complete`,
      {},
    ),
  shelterAnimals: (organizationId: string) =>
    request<ShelterAnimal[]>("GET", `/adoptions/organizations/${organizationId}/animals`),
  intakeShelterAnimal: (
    organizationId: string,
    input: {
      name: string;
      species: "DOG" | "CAT";
      clinicalSex: "MALE" | "FEMALE" | null;
      estimatedBirthDate: string | null;
      size: "SMALL" | "MEDIUM" | "LARGE" | "GIANT" | null;
      breed: string | null;
      color: string | null;
      description: string | null;
      source: string | null;
      internalNotes: string | null;
      zone: string | null;
    },
  ) => request<ShelterAnimal>("POST", `/adoptions/organizations/${organizationId}/animals`, input),
  adoptionListings: (organizationId: string) =>
    request<AdoptionListing[]>("GET", `/adoptions/organizations/${organizationId}/listings`),
  createAdoptionListing: (
    organizationId: string,
    input: {
      petId: string;
      headline: string;
      description: string;
      idealHome?: string;
      publicHealthSummary?: string;
      zone?: string;
      mediaAssetIds: string[];
    },
  ) =>
    request<AdoptionListing>("POST", `/adoptions/organizations/${organizationId}/listings`, input),
  updateAdoptionListing: (
    organizationId: string,
    listingId: string,
    input: Partial<{
      headline: string;
      description: string;
      idealHome: string;
      publicHealthSummary: string;
      zone: string;
      mediaAssetIds: string[];
    }>,
  ) =>
    request<AdoptionListing>(
      "PATCH",
      `/adoptions/organizations/${organizationId}/listings/${listingId}`,
      input,
    ),
  changeAdoptionStatus: (
    organizationId: string,
    listingId: string,
    status: Exclude<AdoptionListing["status"], "DRAFT">,
  ) =>
    request<AdoptionListing>(
      "POST",
      `/adoptions/organizations/${organizationId}/listings/${listingId}/status`,
      { status },
    ),
  adoptionApplications: (organizationId: string) =>
    request<AdoptionApplication[]>(
      "GET",
      `/adoptions/organizations/${organizationId}/applications`,
    ),
  updateAdoptionApplication: (
    organizationId: string,
    id: string,
    input: { status?: AdoptionApplication["status"]; internalNotes?: string | null },
  ) =>
    request<AdoptionApplication>(
      "PATCH",
      `/adoptions/organizations/${organizationId}/applications/${id}`,
      input,
    ),
  dashboard: (organizationId: string) =>
    request<CrmDashboard>("GET", `/crm/organizations/${organizationId}/dashboard`),
  patients: (organizationId: string, search = "") =>
    request<CrmPatient[]>(
      "GET",
      `/crm/organizations/${organizationId}/patients${search ? `?search=${encodeURIComponent(search)}` : ""}`,
    ),
  patient: (organizationId: string, petId: string, query?: PatientHistoryQuery) => {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(query ?? {}))
      if (value !== undefined) search.set(key, String(value));
    return request<CrmPatientDetail>(
      "GET",
      `/crm/organizations/${organizationId}/patients/${petId}${search.size ? `?${search.toString()}` : ""}`,
    );
  },
  patientClinicalDocuments: (organizationId: string, petId: string) =>
    request<ClinicalAttachment[]>(
      "GET",
      `/crm/organizations/${organizationId}/patients/${petId}/clinical-documents`,
    ),
  createConsultation: (
    organizationId: string,
    petId: string,
    input: ConsultationInput,
    idempotencyKey: string,
  ) =>
    request<CrmConsultation>(
      "POST",
      `/crm/organizations/${organizationId}/patients/${petId}/consultations`,
      input,
      { "Idempotency-Key": idempotencyKey },
    ),
  addConsultationAddendum: (
    organizationId: string,
    petId: string,
    consultationId: string,
    content: string,
    idempotencyKey: string,
  ) =>
    request<CrmConsultation>(
      "POST",
      `/crm/organizations/${organizationId}/patients/${petId}/consultations/${consultationId}/addenda`,
      { content },
      { "Idempotency-Key": idempotencyKey },
    ),
  voidConsultation: (
    organizationId: string,
    petId: string,
    consultationId: string,
    reason: string,
    idempotencyKey: string,
  ) =>
    request<CrmConsultation>(
      "POST",
      `/crm/organizations/${organizationId}/patients/${petId}/consultations/${consultationId}/void`,
      { reason },
      { "Idempotency-Key": idempotencyKey },
    ),
  clinicalAttachments: (organizationId: string, petId: string, consultationId: string) =>
    request<ClinicalAttachment[]>(
      "GET",
      `/crm/organizations/${organizationId}/patients/${petId}/consultations/${consultationId}/attachments`,
    ),
  createClinicalAttachmentIntent: (
    organizationId: string,
    petId: string,
    consultationId: string,
    file: File,
    metadata?: { category?: string; title?: string; description?: string; documentDate?: string },
  ) =>
    request<ClinicalUploadIntent>(
      "POST",
      `/crm/organizations/${organizationId}/patients/${petId}/consultations/${consultationId}/attachments/upload-intents`,
      {
        fileName: file.name,
        contentType: file.type,
        sizeBytes: file.size,
        category: metadata?.category ?? "OTHER",
        ...(metadata?.title ? { title: metadata.title } : {}),
        ...(metadata?.description ? { description: metadata.description } : {}),
        ...(metadata?.documentDate ? { documentDate: metadata.documentDate } : {}),
      },
    ),
  completeClinicalAttachment: (
    organizationId: string,
    petId: string,
    consultationId: string,
    assetId: string,
  ) =>
    request<{ assetId: string; status: "READY"; uploadedAt: string }>(
      "POST",
      `/crm/organizations/${organizationId}/patients/${petId}/consultations/${consultationId}/attachments/${assetId}/complete`,
      {},
    ),
  createVaccination: (
    organizationId: string,
    petId: string,
    input: VaccinationInput,
    idempotencyKey: string,
  ) =>
    request<CrmVaccination>(
      "POST",
      `/crm/organizations/${organizationId}/patients/${petId}/vaccinations`,
      input,
      { "Idempotency-Key": idempotencyKey },
    ),
  createMedication: (
    organizationId: string,
    petId: string,
    input: MedicationInput,
    idempotencyKey: string,
  ) =>
    request<CrmMedication>(
      "POST",
      `/crm/organizations/${organizationId}/patients/${petId}/medications`,
      input,
      { "Idempotency-Key": idempotencyKey },
    ),
  stopMedication: (
    organizationId: string,
    petId: string,
    medicationId: string,
    input: {
      status: "COMPLETED" | "DISCONTINUED" | "SUSPENDED" | "CANCELLED";
      reason: string;
    },
    idempotencyKey: string,
  ) =>
    request<CrmMedication>(
      "POST",
      `/crm/organizations/${organizationId}/patients/${petId}/medications/${medicationId}/stop`,
      input,
      { "Idempotency-Key": idempotencyKey },
    ),
  clinicalDrafts: (organizationId: string) =>
    request<CrmClinicalDraft[]>("GET", `/crm/organizations/${organizationId}/clinical-drafts`),
  createClinicalDraft: (
    organizationId: string,
    petId: string,
    input: ClinicalDraftFields & { source: ClinicalDraftSource },
  ) =>
    request<CrmClinicalDraft>(
      "POST",
      `/crm/organizations/${organizationId}/patients/${petId}/clinical-drafts`,
      input,
    ),
  updateClinicalDraft: (
    organizationId: string,
    draftId: string,
    input: ClinicalDraftFields & { revision: number },
  ) =>
    request<CrmClinicalDraft>(
      "PATCH",
      `/crm/organizations/${organizationId}/clinical-drafts/${draftId}`,
      input,
    ),
  discardClinicalDraft: (organizationId: string, draftId: string, revision: number) =>
    request<void>(
      "POST",
      `/crm/organizations/${organizationId}/clinical-drafts/${draftId}/discard`,
      { revision },
    ),
  signClinicalDraft: (
    organizationId: string,
    draftId: string,
    revision: number,
    idempotencyKey: string,
  ) =>
    request<ClinicalDraftSigned>(
      "POST",
      `/crm/organizations/${organizationId}/clinical-drafts/${draftId}/sign`,
      { revision },
      { "Idempotency-Key": idempotencyKey },
    ),
  team: (organizationId: string, workspace: ProWorkspaceType = "VETERINARY") =>
    request<CrmTeamOverview>("GET", teamPath(organizationId, workspace)),
  inviteTeamMember: (
    organizationId: string,
    input: { email: string; role: Exclude<MembershipRole, "OWNER"> },
    workspace: ProWorkspaceType = "VETERINARY",
  ) =>
    request<CrmTeamInvitationCreated>(
      "POST",
      `${teamPath(organizationId, workspace)}/invitations`,
      input,
    ),
  updateTeamMemberRole: (
    organizationId: string,
    membershipId: string,
    role: Exclude<MembershipRole, "OWNER">,
    workspace: ProWorkspaceType = "VETERINARY",
  ) =>
    request<CrmTeamMember>(
      "POST",
      `${teamPath(organizationId, workspace)}/members/${membershipId}/role`,
      { role },
    ),
  revokeTeamMember: (
    organizationId: string,
    membershipId: string,
    workspace: ProWorkspaceType = "VETERINARY",
  ) => request<void>("DELETE", `${teamPath(organizationId, workspace)}/members/${membershipId}`),
  revokeTeamInvitation: (
    organizationId: string,
    invitationId: string,
    workspace: ProWorkspaceType = "VETERINARY",
  ) =>
    request<void>("DELETE", `${teamPath(organizationId, workspace)}/invitations/${invitationId}`),
  acceptTeamInvitation: (token: string) =>
    request<{
      organizationId: string;
      organizationName: string;
      membershipId: string;
      role: MembershipRole;
    }>("POST", "/pro/team-invitations/accept", { token }),
  accessRequests: (organizationId: string) =>
    request<CrmAccess[]>("GET", `/crm/organizations/${organizationId}/access-requests`),
  requestAccess: (
    organizationId: string,
    input: { identifier: string; scope: AccessScope; message?: string },
  ) => request<CrmAccess>("POST", `/crm/organizations/${organizationId}/access-requests`, input),
  locations: (organizationId: string, includeInactive = false) =>
    request<CrmLocation[]>(
      "GET",
      `/crm/organizations/${organizationId}/locations${includeInactive ? "?includeInactive=true" : ""}`,
    ),
  createLocation: (
    organizationId: string,
    input: { name: string; address?: string | null; phone?: string | null },
  ) => request<CrmLocation>("POST", `/crm/organizations/${organizationId}/locations`, input),
  updateLocation: (
    organizationId: string,
    locationId: string,
    input: { name?: string; address?: string | null; phone?: string | null; isActive?: boolean },
  ) =>
    request<CrmLocation>(
      "PATCH",
      `/crm/organizations/${organizationId}/locations/${locationId}`,
      input,
    ),
  tasks: (organizationId: string, status?: CrmTaskStatus) =>
    request<CrmTask[]>(
      "GET",
      `/crm/organizations/${organizationId}/tasks${
        status ? `?status=${encodeURIComponent(status)}` : ""
      }`,
    ),
  createTask: (
    organizationId: string,
    input: {
      title: string;
      description?: string | null;
      priority: CrmTaskPriority;
      dueAt?: string | null;
      petId?: string | null;
      assignedUserId?: string | null;
    },
  ) => request<CrmTask>("POST", `/crm/organizations/${organizationId}/tasks`, input),
  updateTask: (
    organizationId: string,
    taskId: string,
    input: {
      title?: string;
      description?: string | null;
      priority?: CrmTaskPriority;
      dueAt?: string | null;
      assignedUserId?: string | null;
      status?: CrmTaskStatus;
    },
  ) => request<CrmTask>("PATCH", `/crm/organizations/${organizationId}/tasks/${taskId}`, input),
  appointments: (organizationId: string, locationId?: string) =>
    request<CrmAppointment[]>(
      "GET",
      `/crm/organizations/${organizationId}/appointments${
        locationId ? `?locationId=${encodeURIComponent(locationId)}` : ""
      }`,
    ),
  operations: (organizationId: string, day: string, windowDays: 7 | 30, locationId?: string) =>
    request<CrmOperationsOverview>(
      "GET",
      `/crm/organizations/${organizationId}/operations?day=${encodeURIComponent(day)}&windowDays=${windowDays}${
        locationId ? `&locationId=${encodeURIComponent(locationId)}` : ""
      }`,
    ),
  createAppointment: (organizationId: string, petId: string, input: AppointmentInput) =>
    request<CrmOperationalAppointment>(
      "POST",
      `/crm/organizations/${organizationId}/patients/${petId}/appointments`,
      input,
    ),
  transitionAppointment: (
    organizationId: string,
    appointmentId: string,
    status: Exclude<AppointmentStatus, "PLANNED">,
  ) =>
    request<CrmAppointment>(
      "POST",
      `/crm/organizations/${organizationId}/appointments/${appointmentId}/status`,
      { status },
    ),
  completeProfessionalService: (
    organizationId: string,
    appointmentId: string,
    input: { notes?: string; metadata?: Record<string, unknown> } = {},
  ) =>
    request(
      "POST",
      `/crm/organizations/${organizationId}/bookings/${appointmentId}/complete-service`,
      input,
    ),
};

export interface AvailabilityRule {
  id: string;
  active: boolean;
  weekday: number;
  startMinute: number;
  endMinute: number;
  timezone: string;
  capacity: number;
  serviceId: string | null;
  professionalUserId: string | null;
  locationId: string | null;
}
export interface AvailabilityBlock {
  id: string;
  active: boolean;
  startsAt: string;
  endsAt: string;
  reason: string | null;
  professionalUserId: string | null;
  locationId: string | null;
}
export interface AvailabilityConfiguration {
  rules: AvailabilityRule[];
  blocks: AvailabilityBlock[];
  services: { id: string; name: string }[];
  professionals: { userId: string; displayName: string }[];
  locations: { id: string; name: string; timezone: string }[];
  scheduling: {
    timezone: string;
    defaultServiceDurationMinutes: number;
    appointmentBufferMinutes: number;
  };
  notice: string;
}

export async function uploadClinicalFile(
  intent: Pick<ClinicalUploadIntent, "uploadUrl" | "headers">,
  file: File,
): Promise<void> {
  const response = await fetch(intent.uploadUrl, {
    method: "PUT",
    headers: intent.headers,
    body: file,
  });
  if (!response.ok) {
    throw new Error("No pudimos transferir el archivo al almacenamiento privado.");
  }
}

export async function uploadProfessionalMediaFile(
  intent: Pick<ProfessionalMediaUploadIntent, "uploadUrl" | "headers" | "method">,
  file: File,
): Promise<void> {
  const response = await fetch(intent.uploadUrl, {
    method: intent.method,
    headers: intent.headers,
    body: file,
  });
  if (!response.ok) throw new Error("No pudimos transferir la foto del servicio.");
}
